import { formatInTimeZone } from "date-fns-tz";
import { saveAs } from "file-saver";
import { sortBy } from "lodash";
import Papa from "papaparse";

import { logEvent } from "@internal/analytics";
import { type User } from "@internal/user";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  NBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import { type Compare } from "../hooks/use-chart-state";
import { type ChartDatasets } from "../utils/chart-preprocessing";

type Datasets = ChartDatasets["datasets"];

const FIELD_CATEGORY = "Category";
const FIELD_DATE = "Date";

const FORMAT_DATE = "yyyy-MM-dd";
const FORMAT_TIME = "yyyy-MM-ddThh:mm:ssXXX";

function generateColumnName(
  compare: Compare,
  metric: SalesPageMetric,
  user: User,
) {
  if (compare.queryKey === "accountingMode") {
    const label = NBAccountingMode.getLabel(compare.queryValue);
    return `${metric.name} (${label})`;
  }

  if (compare.queryKey === "attributionModel") {
    const label = user.attributionMethodEnum.getLabel(compare.queryValue);
    return `${metric.name} (${label})`;
  }

  return metric.name;
}

export function downloadCsv(
  fileName: string,
  comparisons: Compare[],
  metrics: SalesPageMetric[],
  datasets: [Datasets, Datasets | undefined, Datasets | undefined],
  timeGranularity: ITimeGranularityV2,
  user: User,
) {
  logEvent("Exported chart data to CSV");

  const mainMetric = metrics[0];
  const data: Record<string, any> = {};
  const fields: string[] = [FIELD_CATEGORY, FIELD_DATE, mainMetric.name];

  for (const [i, compare] of comparisons.entries()) {
    if (compare.queryKey === "compareDateRange") {
      continue;
    }

    fields.push(generateColumnName(compare, metrics[i + 1], user));
  }

  for (const { name, series } of datasets[0]) {
    for (const [timestamp, metricPoint] of Object.entries(series)) {
      data[`${timestamp}:${name}`] = {
        timestamp,
        name,
        [mainMetric.name]: mainMetric.calculate(metricPoint),
      };
    }
  }

  for (const [i, compare] of comparisons.entries()) {
    const idx = i + 1;
    const dataset = datasets[idx];

    if (!dataset) {
      continue;
    }

    for (const { name, series } of dataset) {
      for (const [timestamp, metricPoint] of Object.entries(series)) {
        const key = `${timestamp}:${name}`;
        const entry = data[key];
        const metric =
          compare.queryKey === "compareDateRange" ? mainMetric : metrics[idx];
        const metricName = generateColumnName(compare, metric, user);
        const value = metric.calculate(metricPoint);

        if (entry) {
          entry[metricName] = value;
        } else {
          data[key] = { timestamp, name, [metricName]: value };
        }
      }
    }
  }

  const rows: any[] = [];

  for (const [, values] of Object.entries(data)) {
    const { timestamp, name, ...rest } = values;
    const dateFormat = timeGranularity !== "hourly" ? FORMAT_DATE : FORMAT_TIME;

    rows.push({
      [FIELD_CATEGORY]: name,
      [FIELD_DATE]: formatInTimeZone(timestamp, user.timezone, dateFormat),
      ...rest,
    });
  }

  const sortedRows = sortBy(rows, [FIELD_DATE, FIELD_CATEGORY]);

  const csvString = Papa.unparse({ fields: fields, data: sortedRows });
  const blob = new Blob([csvString], { type: "text/plain;charset=utf-8" });
  saveAs(blob, fileName);
}
